<template>
  <div class="chat-history">
    <div class="main" ref="Conversation">
      <div
        :class="['section', item.form, item.activate && 'activate']"
        v-for="(item, index) in history"
        :key="index"
        @mouseenter="item.activate = true"
        @mouseleave="item.activate = false"
      >
        <div class="avatar"></div>
        <div class="wrap">
          <span class="text">
            <vue-markdown toc v-if="item.form === 'left'">
              {{ item.content }}
            </vue-markdown>
            <span v-else>
              {{ item.content }}
            </span>
          </span>
          <div class="tool">
            <a-icon type="copy" title="复制" @click="$copy(item.content)" />
          </div>
        </div>
      </div>
      <!-- 只有当 messages 有内容时才显示打字机组件 -->
      <div v-if="messages.length" class="section left">
        <div class="wrap">
          <span class="text">
            <typewriter-text
              :text="messages.join('')"
              :typing-speed="typingSpeed"
              @typing-completed="handleTypingCompleted"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  chatMessage,
  getMessages,
  stopTask,
  getCommonQuestions,
} from "@/apis/dify";
import VueMarkdown from "vue-markdown";
import TypewriterText from "@/components/TypewriterText.vue";

export default {
  components: {
    VueMarkdown,
    TypewriterText,
  },
  data() {
    return {
      loading: false,
      conversation_id: "", // 对话ID
      task_id: "", // 当前回复任务id
      message_id: "", // 当前消息ID
      currentUser: "gpower",
      messages: [],
      currentAbortController: null, // 新增：用于控制当前请求的中断
      history: [
        {
          form: "left",
          content: "你好，关于通元软件的问题，向我提问吧。",
          activate: false,
        },
      ],
      typingSpeed: 30, // 打字机效果速度
      isWaitingForCompletion: false, // 是否等待打字机效果完成
    };
  },
  created() {
    this.getData();
  },
  methods: {
    scroll() {
      const target = this.$refs.Conversation;
      // 滚动到底部
      this.$nextTick(() => {
        target.scrollTop = target?.scrollHeight;
      });
    },
    // 设置打字机效果速度
    setTypingSpeed(speed) {
      this.typingSpeed = speed;
    },
    getData() {
      const conversation_id = sessionStorage.getItem(
        `${process.env.VUE_APP_NAME}:conversation_id`,
      );
      if (conversation_id) {
        this.conversation_id = conversation_id;
        getMessages({ conversation_id, user: this.currentUser, limit: 100 })
          .then((res) => {
            res.data.map((v) => {
              this.history.push({
                form: "right",
                content: v.query,
                activate: false,
              });
              this.history.push({
                form: "left",
                content: v.answer,
                activate: false,
              });
            });
          })
          .finally(() => {
            this.scroll();
          });
      }
      getCommonQuestions().then((res) => {
        if (res.data?.length) {
          // 注意：原代码中 hotQuestion 未在 data 定义，这里假设父组件处理或仅 emit
          this.$emit("question", res.data.map((item) => ({ content: item.content })));
        }
      });
    },
    storeData() {
      sessionStorage.setItem(
        `${process.env.VUE_APP_NAME}:conversation_id`,
        this.conversation_id,
      );
    },
    
    // 【核心修改】停止聊天
    stopChat() {
      // 1. 立即中断网络请求
      if (this.currentAbortController) {
        this.currentAbortController.abort();
        this.currentAbortController = null;
      }

      // 2. 通知后端停止任务（尽力而为）
      if (this.task_id) {
        stopTask(this.task_id, { user: this.currentUser }).catch(() => {});
      }

      // 3. 立即处理 UI：将已接收的内容直接上屏，停止打字机
      if (this.messages.length > 0) {
        const finalContent = this.messages.join("");
        if (finalContent.trim()) {
          this.history.push({
            form: "left",
            content: finalContent,
            activate: false,
          });
        }
        this.messages = []; // 清空数组，TypewriterText 组件会销毁
      }

      // 4. 重置状态并通知父组件
      this.loading = false;
      this.isWaitingForCompletion = false;
      this.$emit("done");
    },

    // 处理打字机效果完成后的逻辑
    handleTypingCompleted(text) {
      if (this.isWaitingForCompletion) {
        this.history.push({
          form: "left",
          content: text,
          activate: false,
        });
        this.messages = [];
        this.scroll();
        this.loading = false;
        this.isWaitingForCompletion = false;
        this.currentAbortController = null; // 请求正常结束，清空控制器
        this.$emit("done");
        this.storeData();
      }
    },

    // 【核心修改】发送消息
    send(q) {
      // 防止重复发起对话请求
      if (this.loading) return;
      
      // 如果之前有未完成的请求，先中断它
      if (this.currentAbortController) {
        this.currentAbortController.abort();
      }

      // 创建新的 AbortController
      const controller = new AbortController();
      this.currentAbortController = controller;

      this.loading = true;
      const question = q;
      
      this.history.push({ form: "right", content: question, activate: false });
      // 重置消息和等待状态
      this.messages = [];
      this.isWaitingForCompletion = false;
      this.scroll();

      chatMessage(
        {
          query: question,
          user: this.currentUser,
          inputs: {},
          conversation_id: this.conversation_id || "",
          response_mode: "streaming",
        },
        {
          signal: controller.signal, // 传入 signal
          onmessage: (stream) => {
            if (stream.event == "workflow_started") {
              this.conversation_id = stream.conversation_id;
              this.task_id = stream.task_id;
              this.message_id = stream.message_id;
            }
            if (stream.event === "message") {
              this.messages.push(stream.answer);
              this.scroll();
            }
            if (stream.event === "message_end") {
              // 设置等待打字机效果完成的标志
              this.isWaitingForCompletion = true;
            }
          },
          onerror: (err) => {
            // 如果是主动 abort，忽略错误提示
            if (controller.signal.aborted) {
              console.log("Mobile chat stopped by user");
              return;
            }

            this.loading = false;
            this.currentAbortController = null;
            this.$message.error(err);
            this.$emit("done");
          },
        },
      );
    },
  },
};
</script>

<style lang="less" scoped>
/* 样式保持不变 */
.chat-history {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  // height: calc(100% - 20rem);
  .main {
    scroll-behavior: smooth;
    overflow: auto;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 20px;
    .section {
      width: 100%;
      box-sizing: border-box;
      margin: 10px 0;
      display: flex;
      color: rgba(51, 51, 51, 1);
      &.left {
        color: #fff;
        .avatar {
          margin-right: 10px;
          background: url("@/assets/images/mobil_avatar_icon1.png");
        }
        .wrap {
          border-bottom-left-radius: 0;
          background: rgba(12, 38, 83, 0.8);
          position: relative;
          .tool {
            display: none;
            position: absolute;
            right: 5px;
            bottom: 5px;
          }
        }
        &.activate {
          .tool {
            display: block;
            cursor: pointer;
            transition: all 0.3s;
            &:hover {
              color: #1890ff;
            }
          }
        }
      }
      &.right {
        color: white;
        justify-content: flex-end;
        .avatar {
          order: 2;
          margin-left: 10px;
          background: url("@/assets/images/mobil_avatar_icon2.png");
        }
        .wrap {
          order: 1;
          border-bottom-right-radius: 0;
          background: #770917;
          position: relative;
          .tool {
            display: none;
            position: absolute;
            left: -15px;
            bottom: 0;
          }
        }
        &.activate {
          .tool {
            display: block;
            cursor: pointer;
            transition: all 0.3s;
            &:hover {
              color: #1890ff;
            }
          }
        }
      }
      .wrap {
        max-width: 80%;
        width: fit-content;
        display: flex;
        padding: 5px 15px;
        flex-direction: row;
        flex-wrap: nowrap;
        border-radius: 15px;
        justify-content: center;
        align-items: center;
        .text {
          display: block;
          width: 100%;
          font-family: "PingFang SC";
          font-size: 16px;
          line-height: 1.5;
          font-weight: 400;
        }
      }
      .avatar {
        width: 52px;
        height: 52px;
        flex-shrink: 0;
        border-radius: 50%;
        overflow: hidden;
      }
    }
  }
}
</style>
